import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import {
  Box,
  FormControl,
  TextField,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useMsal } from "@azure/msal-react";
import { getApiToken } from "../authUtils";
import UserSelect from "../helpers/UserSelect";
import ContactDetailsFields, {
  EMPTY_CONTACT,
  ContactSummary,
  cleanContacts,
  contactSearchText,
  hasInvalidContacts,
  toContactForm,
} from "../helpers/ContactDetailsFields";
const urlconfig = require("../config/urlconfig");

const EMPTY_FORM = {
  legalClientName: "",
  clientAddress: "",
  clientState: "",
  clientGST: "",
  contacts: [EMPTY_CONTACT],
  cSalesPoc: "",
};

const GST_STATES = [
  { code: "01", name: "Jammu & Kashmir(01)" },
  { code: "02", name: "Himachal Pradesh(02)" },
  { code: "03", name: "Punjab(03)" },
  { code: "04", name: "Chandigarh(04)" },
  { code: "05", name: "Uttarakhand(05)" },
  { code: "06", name: "Haryana(06)" },
  { code: "07", name: "Delhi(07)" },
  { code: "08", name: "Rajasthan(08)" },
  { code: "09", name: "Uttar Pradesh(09)" },
  { code: "10", name: "Bihar(10)" },
  { code: "11", name: "Sikkim(11)" },
  { code: "12", name: "Arunachal Pradesh(12)" },
  { code: "13", name: "Nagaland(13)" },
  { code: "14", name: "Manipur(14)" },
  { code: "15", name: "Mizoram(15)" },
  { code: "16", name: "Tripura(16)" },
  { code: "17", name: "Meghalaya(17)" },
  { code: "18", name: "Assam(18)" },
  { code: "19", name: "West Bengal(19)" },
  { code: "20", name: "Jharkhand(20)" },
  { code: "21", name: "Odisha(21)" },
  { code: "22", name: "Chhattisgarh(22)" },
  { code: "23", name: "Madhya Pradesh(23)" },
  { code: "24", name: "Gujarat(24)" },
  { code: "26", name: "Dadra & Nagar Haveli and Daman & Diu(26)" },
  { code: "27", name: "Maharashtra(27)" },
  { code: "28", name: "Andhra Pradesh(28)" },
  { code: "29", name: "Karnataka(29)" },
  { code: "30", name: "Goa(30)" },
  { code: "31", name: "Lakshadweep(31)" },
  { code: "32", name: "Kerala(32)" },
  { code: "33", name: "Tamil Nadu(33)" },
  { code: "34", name: "Puducherry(34)" },
  { code: "35", name: "Andaman & Nicobar Islands(35)" },
  { code: "36", name: "Telangana(36)" },
  { code: "37", name: "Andhra Pradesh(37)" },
  { code: "38", name: "Ladakh(38)" }
];

// NOTE: props and ref both come from forwardRef's signature below.
// props.onClientMapped is an optional callback UniqueClients passes in
// so it can refresh its list right after a raw name gets mapped/unmapped.
const ClientRegistration = forwardRef((props, ref) => {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];

  const [clientForm, setClientForm] = useState(EMPTY_FORM);
  const [clientTable, setClientTable] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [gstError, setGstError] = useState("");
  const [nameError, setNameError] = useState("");
  const [editingClientId, setEditingClientId] = useState(null);
  const [originalClient, setOriginalClient] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pendingRawName, setPendingRawName] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  // Inline Sales POC edit in the client table: one row at a time
  const [salesPocEdit, setSalesPocEdit] = useState({ id: null, value: "", saving: false });

  // "new" = create a brand new client, "existing" = map/move the raw name
  // onto an already-registered client. Only relevant when pendingRawName is set.
  const [mappingMode, setMappingMode] = useState("new");
  const [selectedExistingClient, setSelectedExistingClient] = useState(null);

  // Set only when editing an ALREADY-mapped raw name (via openEditMapping).
  // Lets us know which client to remove the raw name from if it gets moved/unmapped.
  const [originalMappedClientId, setOriginalMappedClientId] = useState(null);

  // Exposes imperative methods so UniqueClients.js can do:
  //   clientRegRef.current.openWithPrefill(rawName)              — for unmapped names
  //   clientRegRef.current.openEditMapping(rawName, clientId)     — for already-mapped names
  useImperativeHandle(ref, () => ({
    openWithPrefill: (rawName) => {
      setClientForm({ ...EMPTY_FORM, legalClientName: rawName });
      setPendingRawName(rawName);
      setEditingClientId(null);
      setOriginalClient(null);
      setSelectedExistingClient(null);
      setOriginalMappedClientId(null);
      // Default to "existing" if there are clients already registered —
      // most raw names turn out to be variants of a client that already exists.
      setMappingMode(clientTable.length > 0 ? "existing" : "new");
      setOpenModal(true);
    },
    openEditMapping: (rawName, currentClientId) => {
      const current = clientTable.find((c) => c.id === currentClientId) || null;
      setClientForm(EMPTY_FORM);
      setPendingRawName(rawName);
      setEditingClientId(null);
      setOriginalClient(null);
      setMappingMode("existing");
      setSelectedExistingClient(current);
      setOriginalMappedClientId(currentClientId);
      setOpenModal(true);
    },
  }));

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const sendAuthenticatedRequest = async (url, method, body) => {
    const token = await getApiToken(instance, account);
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: body ? JSON.stringify(body) : undefined
    });
    const text = await res.text();
    if (!res.ok) throw new Error(text || "API failed");
    try { return text ? JSON.parse(text) : {}; } catch { return text; }
  };

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const validateGST = (gst, state) => {
    if (!gst) return "";

    gst = gst.trim().toUpperCase();

    const regex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

    if (!regex.test(gst))
      return "Invalid GSTIN";

    const gstState = GST_STATES.find(
      s => s.code === gst.substring(0, 2)
    );

    if (!gstState)
      return "Invalid GST State Code";

    if (state && gstState.name !== state)
      return `GST belongs to ${gstState.name}`;

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientForm((prev) => ({ ...prev, [name]: value }));

     if (name === "legalClientName") {
      const exists = clientTable.some(
        c => c.legalClientName?.trim().toLowerCase() === value.trim().toLowerCase() &&
             c.id !== editingClientId
      );
      setNameError(exists ? "Legal Client Name already exists" : "");
    }

    if (name === "clientGST") {

      const gst = value.toUpperCase();

      const exists = clientTable.some(
        c =>
          c.clientGST?.toUpperCase() === gst &&
          c.id !== editingClientId
      );

      if (exists) {
        setGstError("GST Already Exists");
      } else {
        setGstError(
          validateGST(gst, clientForm.clientState)
        );
      }
    }

  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingClientId(null);
    setOriginalClient(null);
    setGstError("");
    setNameError("");
    setClientForm(EMPTY_FORM);
    setPendingRawName(null);
    setMappingMode("new");
    setSelectedExistingClient(null);
    setOriginalMappedClientId(null);
  };

  const isClientChanged = () =>
    !!originalClient && JSON.stringify(originalClient) !== JSON.stringify(clientForm);

  // ─── API calls ───────────────────────────────────────────────────────────────

  const fetchClients = async () => {
    try {
      const data = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/clients`,
        "GET"
      );
      setClientTable(data.sort((a, b) => b.id - a.id));
    } catch (error) {
      showSnackbar("Failed to fetch clients", "error");
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/clients/${editingClientId}`,
        "PUT",
        {
          legalClientName: clientForm.legalClientName,
          clientAddress: clientForm.clientAddress,
          clientState: clientForm.clientState,
          clientGST: clientForm.clientGST,
          contacts: cleanContacts(clientForm.contacts),
          cSalesPoc: clientForm.cSalesPoc,
        }
      );
      showSnackbar("Client updated successfully");
      handleCloseModal();
      fetchClients();
    } catch(err) {
      const msg = err.message || "";
        showSnackbar(
          msg.startsWith("Contact ") ? msg :
          msg.includes("Legal Client Name") ? "Legal Client Name already exists" :
          msg.includes("GST") ? "GST already exists" :
          "Failed to create client",
          "error"
        );
    } finally {
      setSubmitting(false);
    }
  };

  // Plain "+ Create Client" flow — no raw name involved at all.
  const handleCreateClientOnly = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/clients`,
        "POST",
        {
          legalClientName: clientForm.legalClientName,
          clientAddress: clientForm.clientAddress,
          clientState: clientForm.clientState,
          clientGST: clientForm.clientGST,
          contacts: cleanContacts(clientForm.contacts),
          cSalesPoc: clientForm.cSalesPoc,
          rawClientNames: [],
        }
      );
      showSnackbar("Client created successfully");
      console.log("clientForm", clientForm.cSalesPoc);
      handleCloseModal();
      fetchClients();
    } catch (err) {
      const msg = err.message || "";
      showSnackbar(
        msg.startsWith("Contact ") ? msg :
          msg.includes("Legal Client Name") ? "Legal Client Name already exists" :
          msg.includes("GST") ? "GST already exists" :
            "Failed to create client",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Unified mapping commit: handles map-to-new, map-to-existing, AND moving
  // a raw name from one client to another (when originalMappedClientId is set
  // and the target differs).
  const commitMapping = async () => {
    if (!pendingRawName) return;
    setSubmitting(true);
    try {
      let targetClientId;

      if (mappingMode === "existing") {
        if (!selectedExistingClient) return;
        await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/clients/${selectedExistingClient.id}`,
          "PUT",
          { rawClientNames: [pendingRawName] }
        );
        targetClientId = selectedExistingClient.id;
      } else {
        const created = await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/clients`,
          "POST",
          {
            legalClientName: clientForm.legalClientName,
            clientAddress: clientForm.clientAddress,
            clientState: clientForm.clientState,
            clientGST: clientForm.clientGST,
            contacts: cleanContacts(clientForm.contacts),
            cSalesPoc: clientForm.cSalesPoc,
            rawClientNames: [pendingRawName],
          }
        );
        targetClientId = created.id;
      }

      // If this raw name was previously mapped to a DIFFERENT client, clean up the old mapping
      if (originalMappedClientId && originalMappedClientId !== targetClientId) {
        await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/clients/${originalMappedClientId}/raw-names?name=${encodeURIComponent(pendingRawName)}`,
          "DELETE"
        );
      }

      showSnackbar(originalMappedClientId ? "Mapping updated" : "Client mapped successfully");
      handleCloseModal();
      fetchClients();
      props.onClientMapped?.();
    } catch (err) {
      showSnackbar(err.message || "Failed to save mapping", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Removes the raw name from its current client entirely — no replacement mapping.
  const handleUnmap = async () => {
    if (!originalMappedClientId || !pendingRawName) return;
    setSubmitting(true);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/clients/${originalMappedClientId}/raw-names?name=${encodeURIComponent(pendingRawName)}`,
        "DELETE"
      );
      showSnackbar(`Unmapped "${pendingRawName}"`);
      handleCloseModal();
      fetchClients();
      props.onClientMapped?.();
    } catch (err) {
      showSnackbar(err.message || "Failed to unmap", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const startSalesPocEdit = (client) =>
    setSalesPocEdit({ id: client.id, value: client.cSalesPoc || "", saving: false });

  const cancelSalesPocEdit = () => setSalesPocEdit({ id: null, value: "", saving: false });

  // Sends only cSalesPoc; the update endpoint leaves every other field (and the contacts) as they are
  const saveSalesPoc = async () => {
    const { id, value } = salesPocEdit;
    setSalesPocEdit((prev) => ({ ...prev, saving: true }));
    try {
      const updated = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/clients/${id}`,
        "PUT",
        { cSalesPoc: value }
      );
      setClientTable((prev) => prev.map((c) => (c.id === id ? { ...c, ...updated } : c)));
      showSnackbar("Sales person updated");
      cancelSalesPocEdit();
    } catch (err) {
      showSnackbar(err.message || "Failed to update sales person", "error");
      setSalesPocEdit((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/clients/${id}`,
        "DELETE"
      );
      showSnackbar("Client deleted");
      fetchClients();
    } catch (err) {
      let message = "Delete failed";
      if (err.message?.includes("attached to one or more")) {
        message = "Deletion not allowed — this client is linked to existing cost sheets.";
      } else if (err.message?.includes("linked to") && err.message?.includes("raw client name")) {
        message = "Can't delete — this client is linked to a client name. Unmap it first.";
      }
      showSnackbar(message, "error");
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const isPrimaryDisabled = () => {
    if (submitting) return true;

    if (pendingRawName) {
      if (mappingMode === "existing") {
        if (!selectedExistingClient) return true;
        // No-op if re-selecting the same client it's already mapped to
        if (originalMappedClientId && selectedExistingClient.id === originalMappedClientId) return true;
        return false;
      }
      // mode "new"
      return (
        !clientForm.legalClientName ||
        !clientForm.clientGST || !clientForm.clientState ||
        hasInvalidContacts(clientForm.contacts) ||
        !!gstError || !!nameError
      );
    }

    return (
      !clientForm.legalClientName ||
      !clientForm.clientGST || !clientForm.clientState ||
      hasInvalidContacts(clientForm.contacts) ||
      !!gstError ||
      !!nameError ||
      (editingClientId ? !isClientChanged() : false)
    );
  };

  const handlePrimaryAction = async (e) => {
    if (pendingRawName) {
      await commitMapping();
    } else if (editingClientId) {
      await handleUpdateClient(e);
    } else {
      await handleCreateClientOnly(e);
    }
  };

  const primaryLabel = () => {
    if (submitting) return "Saving...";
    if (pendingRawName) return originalMappedClientId ? "Save Mapping" : "Map Client";
    return editingClientId ? "Update" : "Create";
  };

    const filteredClients = useMemo(() => {
    if (!searchText.trim()) return clientTable;

    const search = searchText.toLowerCase().trim();

    return clientTable.filter((client) => {
      return (
        client.legalClientName?.toLowerCase().includes(search) ||
        client.clientGST?.toLowerCase().includes(search) ||
        contactSearchText(client, client.clientSpoc, client.clientSpocNumber).toLowerCase().includes(search) ||
        client.cSalesPoc?.toLowerCase().includes(search)
      );
    });
  }, [clientTable, searchText]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 4, background: "#f5f7fb", minHeight: "100vh" }}>
      <Typography variant="h4" mb={3} fontWeight="bold">
        Client Management
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 8px 20px rgba(0,0,0,0.08)" }}>
        <Box
          sx={{
           display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
           <Typography variant="h6">Client List</Typography>
          <TextField
            size="small"
            placeholder="Search by Client, GST, Contact Person or Sales POC..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 320 }}
          />

          <Button
            variant="contained"
            onClick={() => setOpenModal(true)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
            }}
          >
            + Create Client
          </Button>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ background: "#f1f3f6" }}>
              <TableCell><b>Legal Client Name</b></TableCell>
              <TableCell><b>Address / State</b></TableCell>
              <TableCell><b>GST</b></TableCell>
              <TableCell><b>Contacts</b></TableCell>
              <TableCell><b>Sales Person</b></TableCell>
              <TableCell align="center"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredClients.length > 0 ? (
              filteredClients
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      {c.legalClientName}
                      {c.rawClientNames?.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {c.rawClientNames.length} raw name{c.rawClientNames.length === 1 ? "" : "s"} mapped
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{c.clientAddress} - {c.clientState}</TableCell>
                    <TableCell>{c.clientGST}</TableCell>
                    <TableCell>
                      <ContactSummary contacts={c.contacts} legacyPerson={c.clientSpoc} legacyPhone={c.clientSpocNumber} />
                    </TableCell>
                    <TableCell sx={{ minWidth: salesPocEdit.id === c.id ? 280 : undefined }}>
                      {salesPocEdit.id === c.id ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <UserSelect
                              label="Sales Person"
                              value={salesPocEdit.value}
                              onChange={(value) => setSalesPocEdit((prev) => ({ ...prev, value }))}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Save sales person"
                            onClick={saveSalesPoc}
                            disabled={salesPocEdit.saving || salesPocEdit.value === (c.cSalesPoc || "")}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Cancel sales person edit"
                            onClick={cancelSalesPocEdit}
                            disabled={salesPocEdit.saving}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <span>{c.cSalesPoc || "-"}</span>
                          <IconButton
                            size="small"
                            aria-label="Edit sales person"
                            onClick={() => startSalesPocEdit(c)}
                            disabled={salesPocEdit.id !== null}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ display: "flex" }}>
                      <IconButton
                        aria-label="Edit client"
                        onClick={() => {
                          const selectedClient = {
                            legalClientName: c.legalClientName || "",
                            clientAddress: c.clientAddress || "",
                            clientState: c.clientState || "",
                            clientGST: c.clientGST || "",
                            contacts: toContactForm(c.contacts, c.clientSpoc, c.clientSpocNumber),
                            cSalesPoc: c.cSalesPoc || ""
                          };
                          setClientForm(selectedClient);
                          setOriginalClient(selectedClient);
                          setEditingClientId(c.id);
                          setPendingRawName(null);
                          setMappingMode("new");
                          setOpenModal(true);
                        }}
                        sx={{ background: "#e3f2fd", mr: 1, "&:hover": { background: "#bbdefb" } }}
                      >
                        <EditOutlinedIcon color="primary" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete client"
                        onClick={() => handleDeleteClient(c.id)}
                        sx={{ background: "#ffe5e5", "&:hover": { background: "#ffcccc" } }}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                      {searchText
                          ? "No matching clients found."
                          : "No clients available."}
                  </Typography>
              </TableCell>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredClients.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* MODAL */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {pendingRawName
            ? originalMappedClientId
              ? `Edit Mapping for "${pendingRawName}"`
              : `Map "${pendingRawName}"`
            : editingClientId
              ? "Edit Client"
              : "Add New Client"}
        </DialogTitle>

        <DialogContent dividers>
          {/* Only show the New/Existing toggle when mapping a raw name */}
          {pendingRawName && (
            <Box sx={{ mb: 3 }}>
              {originalMappedClientId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Currently mapped to <strong>{clientTable.find(c => c.id === originalMappedClientId)?.legalClientName || "—"}</strong>.
                  Choose a different client below to move it, or use Unmap to remove the mapping entirely.
                </Alert>
              )}
              <ToggleButtonGroup
                value={mappingMode}
                exclusive
                fullWidth
                onChange={(e, val) => val && setMappingMode(val)}
                sx={{ mb: 1 }}
              >
                <ToggleButton value="existing" disabled={clientTable.length === 0}>
                  {originalMappedClientId ? "Move to Different Client" : "Map to Existing Client"}
                </ToggleButton>
                <ToggleButton value="new">
                  Create New Client
                </ToggleButton>
              </ToggleButtonGroup>
              {clientTable.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No clients registered yet — you'll need to create one first.
                </Typography>
              )}
            </Box>
          )}

          {pendingRawName && mappingMode === "existing" ? (
            <FormControl fullWidth>
              <Autocomplete
                options={clientTable}
                getOptionLabel={(option) => option.legalClientName || ""}
                value={selectedExistingClient}
                onChange={(_, value) => setSelectedExistingClient(value)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.legalClientName}
                        {option.id === originalMappedClientId && (
                          <Chip label="Current" size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.clientGST} · {option.clientState}
                        {option.rawClientNames?.length > 0 && ` · ${option.rawClientNames.length} raw name(s) mapped`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Legal Client"
                    margin="dense"
                    autoFocus
                  />
                )}
              />
              {selectedExistingClient && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {selectedExistingClient.legalClientName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedExistingClient.clientAddress} · {selectedExistingClient.clientState}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    GST: {selectedExistingClient.clientGST}
                  </Typography>
                  {selectedExistingClient.rawClientNames?.length > 0 && (
                    <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selectedExistingClient.rawClientNames.map((rn) => (
                        <Chip key={rn} label={rn} size="small" />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>
          ) : pendingRawName && mappingMode === "new" ? (
            <FormControl fullWidth>
              <TextField
                label="Legal Client Name"
                name="legalClientName"
                error={!!nameError}
                helperText={nameError}
                value={clientForm.legalClientName || pendingRawName}
                onChange={handleChange}
                margin="dense"
                required
              />
              <TextField
                label="Address"
                name="clientAddress"
                value={clientForm.clientAddress}
                onChange={handleChange}
                margin="dense"
                multiline
                rows={3}
              />
              <Autocomplete
                options={GST_STATES}
                getOptionLabel={(option) => option.name}
                value={GST_STATES.find(s => s.name === clientForm.clientState) || null}
                onChange={(_, value) => {
                  const selectedState = value?.name || "";
                  setClientForm(prev => ({ ...prev, clientState: selectedState }));
                  if (clientForm.clientGST) {
                    setGstError(validateGST(clientForm.clientGST, selectedState));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State" margin="dense" required />
                )}
              />
              <TextField
                label="GST"
                name="clientGST"
                value={clientForm.clientGST}
                onChange={(e) => {
                  handleChange({ target: { name: "clientGST", value: e.target.value.toUpperCase() } });
                }}
                inputProps={{ maxLength: 15 }}
                margin="dense"
                error={!!gstError}
                helperText={gstError}
                required
              />
              <ContactDetailsFields
                contacts={clientForm.contacts}
                onChange={(contacts) => setClientForm((prev) => ({ ...prev, contacts }))}
                disabled={submitting}
              />
              <UserSelect
                label="Assign Sales Person"
                value={clientForm.cSalesPoc}
                onChange={(value) => handleChange({ target: { name: "cSalesPoc", value } })}
              />
            </FormControl>
          ) : (
            <FormControl fullWidth>
              <TextField
                label="Legal Client Name"
                name="legalClientName"
                value={clientForm.legalClientName}
                onChange={handleChange}
                margin="dense"
                error={!!nameError}
                helperText={nameError}
                required
              />
              <TextField
                label="Address"
                name="clientAddress"
                value={clientForm.clientAddress}
                onChange={handleChange}
                margin="dense"
                multiline
                rows={3}
              />
              <Autocomplete
                options={GST_STATES}
                getOptionLabel={(option) => option.name}
                value={GST_STATES.find(s => s.name === clientForm.clientState) || null}
                onChange={(_, value) => {
                  const selectedState = value?.name || "";
                  setClientForm(prev => ({ ...prev, clientState: selectedState }));
                  if (clientForm.clientGST) {
                    setGstError(validateGST(clientForm.clientGST, selectedState));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State" margin="dense" required />
                )}
              />
              <TextField
                label="GST"
                name="clientGST"
                value={clientForm.clientGST}
                onChange={(e) => {
                  handleChange({ target: { name: "clientGST", value: e.target.value.toUpperCase() } });
                }}
                inputProps={{ maxLength: 15 }}
                margin="dense"
                error={!!gstError}
                helperText={gstError}
                required
              />
              <ContactDetailsFields
                contacts={clientForm.contacts}
                onChange={(contacts) => setClientForm((prev) => ({ ...prev, contacts }))}
                disabled={submitting}
              />
              <UserSelect
                  label="Assign Sales Person"
                  value={clientForm.cSalesPoc}
                  onChange={(value) => handleChange({ target: { name: "cSalesPoc", value } })}
                />
            </FormControl>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: originalMappedClientId ? "space-between" : "flex-end", px: 3 }}>
          {originalMappedClientId ? (
            <Button
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={handleUnmap}
              disabled={submitting}
            >
              Unmap
            </Button>
          ) : <span />}
          <Box>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePrimaryAction}
              disabled={isPrimaryDisabled()}
            >
              {primaryLabel()}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ClientRegistration;